import { useState, useEffect, useMemo, useRef } from 'react';
import { useAgents } from './useAgents';
import { reconcileSelection, buildAgentFilter } from '../utils/insights/agentSelection';

// Referencia estable para «sin filtro de asesores» (evita refetch por un [] nuevo en cada render)
const NO_AGENTS = [];

/**
 * SS-537 · Selección de asesores del Resumen CX: estado + reconciliación al cargar la lista.
 * - Primera carga y cambio de rango: todos los asesores tildados.
 * - Cambio de supervisor: con todos tildados se tildan todos los nuevos; con selección parcial se
 *   conservan los que siguen disponibles (no se suman los nuevos).
 * - Hacia el backend `[]` es «sin filtro»: con todos tildados no viaja la lista. `agentsNone`
 *   (ninguno tildado) es «sin resultados» y no debe consultarse.
 *
 * @param {Object|null} dateRange - Rango de fechas con startDate y endDate
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Function} t - Función de traducción
 * @returns {Object} { agents, selectedAgents, setSelectedAgents, effectiveAgents, agentsNone }
 */
export function useAgentSelection(dateRange, selectedSupervisor, t) {
  const agents = useAgents(dateRange, selectedSupervisor, t);
  const [selectedAgents, setSelectedAgents] = useState([]);
  // Asesores disponibles en la última carga: base para reconciliar la selección
  const prevAvailableRef = useRef([]);

  // Nuevo rango de fechas: la próxima lista de asesores es una «primera carga» (todos tildados)
  useEffect(() => {
    prevAvailableRef.current = [];
    setSelectedAgents([]);
  }, [dateRange]);

  const availableValues = useMemo(() => agents.agents.map((a) => a.value), [agents.agents]);
  useEffect(() => {
    const previousAvailable = prevAvailableRef.current;
    prevAvailableRef.current = availableValues;
    setSelectedAgents((previousSelected) =>
      reconcileSelection(previousSelected, previousAvailable, availableValues)
    );
  }, [availableValues]);

  const agentFilter = useMemo(
    () => buildAgentFilter(selectedAgents, availableValues),
    [selectedAgents, availableValues]
  );
  const effectiveAgents = agentFilter.agents.length > 0 ? agentFilter.agents : NO_AGENTS;

  return { agents, selectedAgents, setSelectedAgents, effectiveAgents, agentsNone: agentFilter.none };
}
